import { fetchAndRegisterI18nTranslations, translate as t } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { getCurrentOrgName, isManualTranslation, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAdminContext } from "../../../contexts/context";
import { useFetchPersonalisedViews, useFetchPinboardDetail, useGetPinboardListQuery, } from "../../../services/pinboard-list/pinboard-list-service";
import { updateConfigInfo } from "../../../services/system/system-service";
import { create } from '../../../utils/logger';
import { downloadLiveboardTranslations, extractTranslatableContent, } from './liveboard-translation-download.util';
import { ManualTranslationToggle } from './manual-translation-toggle';
import { useTranslationUpload } from './translation-upload.hook';
const logger = create('Admin.ManualTranslation');
const transformToLiveboardData = (pinboard, fallbackName, views = []) => {
    return {
        name: pinboard?.name || fallbackName,
        description: pinboard?.description || null,
        containers: pinboard?.containers?.map(container => ({
            name: container?.name || null,
            description: container?.description || null,
            type: container?.type || null,
            noteTile: container?.noteTile
                ? {
                    htmlParsedString: container.noteTile.htmlParsedString || null,
                }
                : null,
        })) || [],
        tabs: pinboard?.tabs?.map(tab => ({
            name: tab?.name || null,
        })) || [],
        views: views.map(v => ({ name: v.name || null })),
    };
};
export const ManualTranslationToggleContainer = ({ orgViewModeType, onEditingChange, onLoadingChange, onSavingChange, onShowToast, }) => {
    const { sessionService } = useAdminContext();
    const isOrgEnabled = sessionService.isOrgUiEnabled();
    const isAllOrgContext = !isOrgEnabled || orgViewModeType === 'all-org';
    const isMountedRef = useRef(true);
    const [enabled, setEnabled] = useState(false);
    const [clusterEnabled, setClusterEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [pendingValue, setPendingValue] = useState(false);
    const [selectedLiveboards, setSelectedLiveboards] = useState([]);
    const [selectorSearchPattern, setSelectorSearchPattern] = useState('');
    const [debouncedPattern, setDebouncedPattern] = useState('');
    const availableLiveboardsRef = useRef([]);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const { uploadCSV, uploading, uploadError, clearUploadError, checkCsvExists, downloadExistingCsv, downloadingExistingCsv, existingCsvDownloadError, clearExistingCsvDownloadError, deleteCSV, deleting, deleteError, clearDeleteError, } = useTranslationUpload(isAllOrgContext);
    const [csvExists, setCsvExists] = useState(null);
    const canModify = isAllOrgContext || clusterEnabled;
    const perOrgMapRef = useRef({});
    const releaseVersion = sessionService.getBaseReleaseVersion();
    const LEARN_MORE_URL = `https://docs.thoughtspot.com/cloud/${releaseVersion}/translation`;
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedPattern(selectorSearchPattern), 300);
        return () => clearTimeout(timer);
    }, [selectorSearchPattern]);
    const { data: pinboardListData, loading: pinboardListLoading, } = useGetPinboardListQuery({ batchSize: 1000, offset: 0, pattern: debouncedPattern || undefined }, !isEditing || !pendingValue);
    const { fetchPinboardDetail } = useFetchPinboardDetail();
    const { fetchPersonalisedViews } = useFetchPersonalisedViews();
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const fetchConfig = () => {
        const configInfo = sessionService.getSessionInfo()?.configInfo;
        const clusterValue = Boolean(configInfo?.manualTranslationOfObjectMetadata);
        const sessionPerOrg = configInfo?.manualTranslationOfObjectMetadataPerOrg || {};
        perOrgMapRef.current = isAllOrgContext
            ? sessionPerOrg
            : { ...perOrgMapRef.current, ...sessionPerOrg };
        setClusterEnabled(clusterValue);
        if (isAllOrgContext) {
            setEnabled(clusterValue);
            setPendingValue(clusterValue);
        }
        else if (!clusterValue) {
            setEnabled(false);
            setPendingValue(false);
        }
        else {
            const orgId = sessionService.getCurrentOrgId().toString();
            const overrideValue = perOrgMapRef.current[orgId] !== undefined
                ? Boolean(perOrgMapRef.current[orgId])
                : undefined;
            const effectiveValue = overrideValue !== undefined ? overrideValue : clusterValue;
            setEnabled(effectiveValue);
            setPendingValue(effectiveValue);
        }
        setLoading(false);
    };
    useEffect(() => {
        fetchConfig();
    }, [orgViewModeType]);
    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    useEffect(() => {
        onSavingChange?.(saving);
    }, [saving, onSavingChange]);
    const performSave = useCallback(async (newValue) => {
        try {
            setSaving(true);
            setSaveError(null);
            let apiChanges;
            let sessionUpdate;
            if (isAllOrgContext) {
                apiChanges = {
                    manualTranslationOfObjectMetadata: newValue,
                    ...(newValue && {
                        manualTranslationOfObjectMetadataPerOrg: {},
                    }),
                };
                sessionUpdate = { ...apiChanges };
                if (newValue)
                    perOrgMapRef.current = {};
            }
            else {
                const orgId = sessionService.getCurrentOrgId().toString();
                perOrgMapRef.current = {
                    ...perOrgMapRef.current,
                    [orgId]: newValue,
                };
                apiChanges = {
                    manualTranslationOfObjectMetadataPerOrg: {
                        [orgId]: newValue,
                    },
                };
                sessionUpdate = {
                    manualTranslationOfObjectMetadataPerOrg: {
                        ...perOrgMapRef.current,
                    },
                };
            }
            await updateConfigInfo(apiChanges);
            if (!isMountedRef.current)
                return false;
            setEnabled(newValue);
            setPendingValue(newValue);
            if (isAllOrgContext)
                setClusterEnabled(newValue);
            const sessionInfo = sessionService.getSessionInfo();
            if (sessionInfo) {
                sessionService.setSessionInfo({
                    ...sessionInfo,
                    configInfo: {
                        ...sessionInfo.configInfo,
                        ...sessionUpdate,
                    },
                });
            }
            return true;
        }
        catch (error) {
            logger.warn('Manual translation config save failed', error);
            if (isMountedRef.current) {
                setSaveError(t('adminSection.manualTranslation.saveError'));
            }
            return false;
        }
        finally {
            if (isMountedRef.current) {
                setSaving(false);
            }
        }
    }, [isAllOrgContext, sessionService]);
    const handleSave = async (newValue) => {
        const success = await performSave(newValue);
        if (success && isMountedRef.current) {
            setIsEditing(false);
            onEditingChange?.(false);
            onShowToast?.(t('tscli.ui.appsettings.datasave.success.message'));
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE), {
                newStatus: newValue ? 'enabled' : 'disabled',
                oldStatus: enabled ? 'enabled' : 'disabled',
                org: isAllOrgContext ? 'All Orgs' : getCurrentOrgName(),
            });
        }
    };
    const handleUploadCSV = useCallback(async (file) => {
        if (!enabled && pendingValue) {
            const saveSuccess = await performSave(pendingValue);
            if (!saveSuccess) {
                return { success: false, error: t('adminSection.manualTranslation.saveError') };
            }
        }
        const text = await file.text();
        const dataRows = text.split('\n').filter(line => line.trim() !== '').length - 1;
        const result = await uploadCSV(file);
        if (result.success) {
            collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_UPLOAD_NEW_CSV), { noOfRowsInCSV: Math.max(0, dataRows) });
            setCsvExists(true);
            if (isManualTranslation()) {
                fetchAndRegisterI18nTranslations().catch(err => {
                    logger.warn('Failed to refresh manual translations after CSV upload', err);
                });
            }
        }
        return result;
    }, [enabled, pendingValue, performSave, uploadCSV]);
    const startEditing = () => {
        if (!canModify || saving) {
            return;
        }
        setPendingValue(enabled);
        setSelectedLiveboards([]);
        setSelectorSearchPattern('');
        setDebouncedPattern('');
        clearUploadError();
        clearExistingCsvDownloadError();
        clearDeleteError();
        setSaveError(null);
        setDownloadError(null);
        setIsEditing(true);
        onEditingChange?.(true);
        setCsvExists(null);
        (async () => {
            const exists = await checkCsvExists();
            if (isMountedRef.current) {
                setCsvExists(exists);
            }
        })().catch(() => { });
    };
    const cancelEditing = () => {
        setPendingValue(enabled);
        setSelectedLiveboards([]);
        setSelectorSearchPattern('');
        setDebouncedPattern('');
        clearUploadError();
        clearExistingCsvDownloadError();
        clearDeleteError();
        setSaveError(null);
        setDownloadError(null);
        setIsEditing(false);
        onEditingChange?.(false);
    };
    const handleToggleChange = (value) => {
        setPendingValue(value);
    };
    const handleSaveClick = () => {
        handleSave(pendingValue);
    };
    const handleSelectorSearch = useCallback((pattern) => {
        setSelectorSearchPattern(pattern);
        if (!pattern) {
            setDebouncedPattern('');
        }
    }, []);
    const handleSelectLiveboards = useCallback((liveboards) => {
        setSelectedLiveboards(prev => {
            const inCurrentOptions = new Set(availableLiveboardsRef.current.map(lb => lb.id));
            const preserved = prev.filter(lb => !inCurrentOptions.has(lb.id));
            return [...preserved, ...liveboards];
        });
        setDownloadError(null);
    }, []);
    const handleRemoveLiveboard = useCallback((idToRemove) => {
        setSelectedLiveboards(prev => prev.filter(lb => lb.id !== idToRemove));
    }, []);
    const handleReplaceCsvClick = useCallback(() => {
        collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_REPLACE_EXISTING_CSV));
    }, []);
    const handleDeleteCsv = useCallback(async () => {
        const result = await deleteCSV();
        if (result.success) {
            setCsvExists(false);
        }
    }, [deleteCSV]);
    const handleDownload = useCallback(async () => {
        if (selectedLiveboards.length === 0) {
            return;
        }
        setDownloading(true);
        setDownloadError(null);
        try {
            const results = await Promise.allSettled(selectedLiveboards.map(async (liveboard) => {
                const [result, views] = await Promise.all([
                    fetchPinboardDetail(liveboard.id),
                    fetchPersonalisedViews(liveboard.id).catch(() => []),
                ]);
                if (!result.pinboard) {
                    throw new Error(liveboard.name);
                }
                return transformToLiveboardData(result.pinboard, liveboard.name, views);
            }));
            const succeeded = results
                .filter((r) => r.status === 'fulfilled')
                .map(r => r.value);
            const failedNames = selectedLiveboards
                .filter((_, i) => results[i].status === 'rejected')
                .map(lb => lb.name);
            if (succeeded.length > 0) {
                await downloadLiveboardTranslations(succeeded);
                const totalRows = succeeded.reduce((sum, lb) => sum + extractTranslatableContent(lb).length, 0);
                collectEvent(new UserAction(userActionTypeEnum.MANUAL_CSV_TRANSLATE_DOWNLOAD_METADATA), {
                    noOfLiveboards: selectedLiveboards.length,
                    noOfRowsInCSV: totalRows,
                });
            }
            if (failedNames.length > 0) {
                logger.warn('Some liveboards failed to load', failedNames);
                setDownloadError(`${t('adminSection.manualTranslation.downloadError')}: ${failedNames.join(', ')}`);
            }
        }
        catch (error) {
            logger.warn('Manual translation download failed', error);
            setDownloadError(t('adminSection.manualTranslation.downloadError'));
        }
        finally {
            if (isMountedRef.current) {
                setDownloading(false);
            }
        }
    }, [selectedLiveboards, fetchPinboardDetail, fetchPersonalisedViews]);
    const availableLiveboards = React.useMemo(() => {
        const objects = pinboardListData?.getPinboardList?.objects || [];
        const result = objects.map((lb) => ({
            id: lb.id,
            name: lb.name || lb.id,
        }));
        availableLiveboardsRef.current = result;
        return result;
    }, [pinboardListData]);
    if (loading) {
        return null;
    }
    return (React.createElement(ManualTranslationToggle, { enabled: enabled, pendingValue: pendingValue, onToggleChange: handleToggleChange, onSave: handleSaveClick, onEdit: startEditing, onCancel: cancelEditing, isEditing: isEditing, isClusterContext: isAllOrgContext, clusterEnabled: clusterEnabled, learnMoreUrl: LEARN_MORE_URL, saving: saving, canModify: canModify, onUploadCSV: handleUploadCSV, uploading: uploading, uploadError: uploadError, onClearUploadError: clearUploadError, saveError: saveError, csvExists: csvExists, onDownloadExistingCsv: downloadExistingCsv, onReplaceCsvClick: handleReplaceCsvClick, downloadingExistingCsv: downloadingExistingCsv, existingCsvDownloadError: existingCsvDownloadError, onDeleteCsv: handleDeleteCsv, deleting: deleting, deleteError: deleteError, onClearDeleteError: clearDeleteError, selectedLiveboards: selectedLiveboards, availableLiveboards: availableLiveboards, selectorLoading: pinboardListLoading, downloading: downloading, downloadError: downloadError, onSelectorSearch: handleSelectorSearch, onSelectLiveboards: handleSelectLiveboards, onRemoveLiveboard: handleRemoveLiveboard, onDownload: handleDownload }));
};
//# sourceMappingURL=manual-translation-toggle.container.js.map